import {Store} from "./models";
import favouriteStoreIcon from "../../assets/images/png/favouriteStore.png";
import storeIconSmall from "../../assets/images/png/store_icon_small.png";
import storeIconBig from "../../assets/images/png/store_icon_big.png";
import gps from "../../assets/images/svg/gps_icon.svg";
import {MarkerClusterer, SuperClusterAlgorithm} from "@googlemaps/markerclusterer";
import {CustomRenderer} from "./renderer";

class MarkerController {

  // =========================
  // 🔹 MARKER CREATION
  // =========================

  createMarker(
    map: google.maps.Map | undefined,
    store: Store
  ): google.maps.marker.AdvancedMarkerElement {
    let title = store.address_line_1;

    if (store.address_line_2) {
      title += ', ' + store.address_line_2;
    }
    if (store.address_line_3) {
      title += ', ' + store.address_line_3;
    }

    const iconClass = store.isFavourite ? favouriteStoreIcon : storeIconSmall;

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: new google.maps.LatLng(store.lat, store.lng),
      title: title,
      content: this.createMarkerContent(iconClass, 20),
      map: map,
    });

    store.marker = marker;
    return marker;
  }

  createMarkers(
    map: google.maps.Map | undefined,
    stores: Store[],
    onSelectStore: (store: Store) => void
  ): google.maps.marker.AdvancedMarkerElement[] {
    return stores.map(store => {
      const marker = this.createMarker(map, store);
      marker.addListener('click', () => onSelectStore(store));
      return marker;
    });
  }

  createPositionMarker(
    currentPosition: google.maps.LatLng,
    map?: google.maps.Map
  ) {
    return new google.maps.marker.AdvancedMarkerElement({
      position: currentPosition,
      title: "Position",
      content: this.createMarkerContent(gps, 20, 0, true),
      map: map,
    });
  }

  // =========================
  // 🔹 MARKER CONTENT GENERATION
  // =========================

  createMarkerContent(
    src: string,
    size: number,
    count?: number,
    addPulsingCircle?: boolean
  ): HTMLElement {
    const container = document.createElement('div');
    container.style.position = 'relative';
    container.style.justifyContent = 'center';
    container.style.display = 'flex';
    container.style.alignItems = 'center';

    if (addPulsingCircle) {
      const pulsingCircle = document.createElement('svg');
      pulsingCircle.classList.add('pulsing-circle');
      pulsingCircle.setAttribute('width', '48');
      pulsingCircle.setAttribute('height', '48');
      pulsingCircle.setAttribute('viewBox', '0 0 48 48');

      const circle = document.createElement('circle');
      circle.setAttribute('cx', '24');
      circle.setAttribute('cy', '24');
      circle.setAttribute('r', '10');
      pulsingCircle.appendChild(circle);

      container.appendChild(pulsingCircle);
    }

    const icon = document.createElement('img');
    icon.src = src;
    icon.width = size;
    container.appendChild(icon);

    if (count && count > 0) {
      const countText = document.createElement('span');
      countText.innerText = String(count);
      countText.style.position = 'absolute';
      countText.style.color = 'white';
      countText.style.fontWeight = 'bold';
      container.appendChild(countText);
    }

    return container;
  }

  // =========================
  // 🔹 MARKER STATE MANAGEMENT
  // =========================

  handleMarkerChanged(
    selectedStore: Store,
    currentStore?: Store
  ) {
    if (selectedStore === currentStore) {
      if (!selectedStore.isFavourite) {
        selectedStore.marker.content = this.createMarkerContent(storeIconBig, 40);
      }
      return;
    }

    if (selectedStore.isFavourite && currentStore) {
      currentStore.marker.content = this.createMarkerContent(storeIconSmall, 20);
      return;
    }

    selectedStore.marker.content = this.createMarkerContent(storeIconBig, 40);

    if (!currentStore?.isFavourite && currentStore?.marker) {
      currentStore.marker.content = this.createMarkerContent(storeIconSmall, 20);
    }
  }

  resetStore(
    stores: Store[],
    id?: string
  ) {
    const store = stores.find(it => it.id === id);
    if (store) {
      store.isFavourite = false;
      store.marker.content = this.createMarkerContent(storeIconSmall, 20);
    }
  }

  resetIcon(
    store?: Store,
  ) {
    if (store && !store.isFavourite) {
      store.marker.content = this.createMarkerContent(storeIconSmall, 20);
    }
  }

  // =========================
  // 🔹 MARKER CLUSTERING
  // =========================

  createMarkerCluster(
    markers?: google.maps.marker.AdvancedMarkerElement[],
    map?: google.maps.Map
  ) {
    return new MarkerClusterer({
      renderer: new CustomRenderer(),
      markers: markers,
      map: map,
      algorithm: new SuperClusterAlgorithm({
        maxZoom: 16,
        viewportPadding: 0,
        maxDistance: 512,
        gridSize: 64,
        minZoom: 1,
        minPoints: 3,
        radius: 70,
        extent: 300,
        nodeSize: 64,
      }),
    });
  }
}

export const markerController = new MarkerController();
